import { AREA_LIST_ITEM_DROP_TYPE, IRestaurantArea, ListItem, Pill, TPillTone } from '@localina/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DraggableItemWrapper, IDraggableItemWrapperProps } from '../../../utils/SortableItemsUtils';
import { AreaCapacityPie, IAreaCapacitySlice } from './AreaCapacityPie';

interface IProps extends TDraggableProps {
    area: IRestaurantArea;
    onClick: (areaDetails: IRestaurantArea, virtualAreaId: string) => void;
    virtualAreaId: string;

    virtualAreaName?: string;

    /** All areas' capacities, for the share pie; left out when there is only one area. */
    capacitySlices?: IAreaCapacitySlice[];
}

type TDraggableProps = Pick<IDraggableItemWrapperProps, 'findItemIndex' | 'moveItem' | 'onDrop' | 'index'>;

export const AreaListItem: React.FC<IProps> = (props) => {
    const { t } = useTranslation();

    const handleClick = () => {
        if (props.area) {
            props.onClick(props.area, props.virtualAreaId);
        }
    };

    const { capacity, tablePlans, areaShifts } = props.area;
    const plan = tablePlans?.[0];

    // Only reservation times that release seats online count; 0% hides the area there.
    const onlineShifts = (areaShifts ?? []).filter((shift) => shift.capacityPercentage > 0);
    let status: { label: string; tone: TPillTone; extraClassName?: string };
    if (onlineShifts.length === 0) {
        status = { label: t('sectors.notOnline'), tone: 'neutral', extraClassName: 'is-off' };
    } else if (onlineShifts.some((shift) => shift.directlyBookable)) {
        status = { label: t('sectors.bookableOnline'), tone: 'good' };
    } else {
        status = { label: t('sectors.notBookableOnline'), tone: 'neutral' };
    }

    const totalCapacity = props.capacitySlices?.reduce((sum, slice) => sum + Math.max(slice.capacity, 0), 0) ?? 0;
    const share = totalCapacity > 0 ? Math.round((Math.max(capacity, 0) / totalCapacity) * 100) : undefined;
    const shareLabel = share !== undefined ? t('sectors.capacityShare', { percent: share }) : undefined;

    return (
        <DraggableItemWrapper
            id={props.area.id}
            moveItem={props.moveItem}
            index={props.index}
            findItemIndex={props.findItemIndex}
            itemType={AREA_LIST_ITEM_DROP_TYPE}
            onDrop={props.onDrop}
        >
            <ListItem
                classNames="area-listitem"
                leftIcon={
                    <span className="area-listitem__handle">
                        <DragIndicatorIcon />
                    </span>
                }
                leftLabel={`${+props.index + 1}`}
                title={
                    <div className="area-listitem__row">
                        <div className="area-listitem__main">
                            <span className="area-listitem__name">{props.area.name}</span>
                            <span className="area-listitem__where">
                                {[
                                    props.virtualAreaName,
                                    plan ? t('sectors.floorPlan', { name: plan.name }) : t('sectors.noFloorPlan'),
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </span>
                        </div>
                        <div className="area-listitem__capacity">
                            {props.capacitySlices && shareLabel && (
                                <AreaCapacityPie
                                    slices={props.capacitySlices}
                                    activeId={props.area.id}
                                    label={shareLabel}
                                />
                            )}
                            <span className="area-listitem__capacity__text">
                                {shareLabel && (
                                    <span className="area-listitem__share" aria-hidden="true">
                                        {shareLabel}
                                    </span>
                                )}
                                <span className="area-listitem__seats">{t('sectors.seats', { count: capacity })}</span>
                            </span>
                        </div>
                        <Pill tone={status.tone} label={status.label} extraClassName={status.extraClassName} />
                    </div>
                }
                onClick={handleClick}
                disableRipple
            />
        </DraggableItemWrapper>
    );
};
